// Contrail V3.9.2 — "Stopped · 1 h 12 min": how long a device has been stopped.
// The database keeps device_state.stopped_since (trigger, migration 0016): set at the first fix below the moving speed
// after it was moving, cleared when it moves again. For devices that were already parked when 0016 went in (no value
// yet), the end of the last trip in the past 24 h is looked up once from history; none in 24 h → "24h+".
import { useEffect, useState } from "react";
import type { DeviceState } from "./supabase";
import { statusOf } from "./format";
import { fetchHistoryRange } from "./history";

export type StoppedFor = { since: number | null; over24h: boolean } | null;

const cache = new Map<string, { at: number; since: number | null; over24h: boolean } | "loading">();

/** Compact, to fit beside the pill: "<1 min", "12 min", "1h 05m", "2d 3h" */
export function fmtStopped(ms: number) {
  const min = Math.floor(Math.max(0, ms) / 60000);
  if (min < 1) return "<1 min";
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60), m = min % 60;
  if (h < 24) return `${h}h ${String(m).padStart(2, "0")}m`;
  const d = Math.floor(h / 24), hh = h % 24;
  return hh ? `${d}d ${hh}h` : `${d}d`;
}

/** Text to show next to the Stopped pill ("" when not stopped or not known yet). */
export function stoppedLabel(v: StoppedFor, now: number) {
  if (!v) return "";
  if (v.since != null) return fmtStopped(now - v.since);
  return v.over24h ? "24h+" : "";
}

/** Since when this device has been stopped (only while its status is "stopped"). */
export function useStoppedFor(deviceId: string | null | undefined, s: DeviceState | null | undefined, now: number): StoppedFor {
  const stopped = !!deviceId && statusOf(s ?? null, now) === "stopped";
  const fromDb = stopped && s?.stopped_since ? Date.parse(s.stopped_since) : null;
  const [, bump] = useState(0);
  const need = stopped && fromDb == null;
  useEffect(() => {
    if (!need || !deviceId) return;
    const c = cache.get(deviceId);
    if (c === "loading" || (c && Date.now() - c.at < 10 * 60_000)) return;
    cache.set(deviceId, "loading");
    const to = new Date(), from = new Date(to.getTime() - 24 * 3600_000);
    fetchHistoryRange(deviceId, from.toISOString(), to.toISOString())
      .then((h) => {
        const lastTrip = h.trips.length ? Date.parse(h.trips[h.trips.length - 1].end) : null;
        let since = lastTrip;
        if (since == null) {
          const i = h.points.map((p) => p[3] >= 3).lastIndexOf(true);
          if (i >= 0) since = h.points[Math.min(i + 1, h.points.length - 1)][2];
        }
        cache.set(deviceId, { at: Date.now(), since, over24h: since == null });
      })
      .catch(() => cache.set(deviceId, { at: Date.now(), since: null, over24h: false }))
      .finally(() => bump((n) => n + 1));
  }, [need, deviceId]);
  if (!stopped) return null;
  if (fromDb != null) return { since: fromDb, over24h: false };
  const c = deviceId ? cache.get(deviceId) : undefined;
  return c && c !== "loading" ? { since: c.since, over24h: c.over24h } : { since: null, over24h: false };
}
